/**
 * Showcase API Utilities
 * Handles CRUD + publish operations for Welcome Showcase carousels.
 *
 * Showcase carousels are admin-only demo carousels that visitors see
 * when they first open the app.  Only one can be published at a time.
 */

import { normalizeCarouselUrls } from './urlNormalizer';
import { getAuthHeaders } from './authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Create a new showcase carousel (admin only).
 * @param {string} name - Carousel name
 * @param {Array} slides - Initial slides (optional)
 * @returns {Promise<Object>}
 */
export async function createShowcaseCarousel(name = 'Showcase', slides = []) {
  const response = await fetch(`${BACKEND_URL}/api/showcase/create`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ name, slides }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create showcase' }));
    throw new Error(error.detail || 'Failed to create showcase');
  }

  return response.json();
}

/**
 * Publish a showcase carousel (admin only).
 * Un-publishes all other showcase carousels.
 * @param {string} id - Carousel ID
 * @returns {Promise<Object>}
 */
export async function publishShowcase(id) {
  const response = await fetch(`${BACKEND_URL}/api/showcase/${id}/publish`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to publish' }));
    throw new Error(error.detail || 'Failed to publish');
  }

  return response.json();
}

/**
 * Un-publish a showcase carousel (admin only).
 * @param {string} id - Carousel ID
 * @returns {Promise<Object>}
 */
export async function unpublishShowcase(id) {
  const response = await fetch(`${BACKEND_URL}/api/showcase/${id}/unpublish`, {
    method: 'POST',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to unpublish' }));
    throw new Error(error.detail || 'Failed to unpublish');
  }

  return response.json();
}

/**
 * Delete a showcase carousel (admin only).
 * Published showcase carousels cannot be deleted.
 * @param {string} id - Carousel ID
 * @returns {Promise<void>}
 */
export async function deleteShowcase(id) {
  const response = await fetch(`${BACKEND_URL}/api/showcase/${id}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete showcase' }));
    throw new Error(error.detail || 'Failed to delete showcase');
  }
}

/**
 * Fetch the currently published showcase's slides (no auth required).
 * Used by all visitors to load the default/demo content.
 * @returns {Promise<Object|null>} { slides, template, logo_*, name } or null
 */
export async function fetchPublishedShowcase() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/showcase/published`);
    if (!response.ok) return null;
    const data = await response.json();
    return {
      ...data,
      slides: normalizeCarouselUrls(data.slides || []),
    };
  } catch {
    return null;
  }
}
